import { defineStore } from 'pinia';
import {
  ref,
  computed,
} from 'vue';

export const useDeviceDetectionStore = defineStore('deviceDetection', () => {
  // Estado reactivo
  const userAgent = ref('');
  const screenWidth = ref(0);
  const screenHeight = ref(0);
  const devicePixelRatio = ref(1);
  const touchSupport = ref(false);
  const orientation = ref('');
  const maxTouchPoints = ref(0);
  const isInitialized = ref(false);

  // Computed para determinar el tipo de dispositivo
  const isMobile = computed(() => {
    if (!isInitialized.value) return false;

    // Detección por User Agent para móviles (excluyendo iPad)
    const mobileUserAgents = [
      /Android.*Mobile/i,
      /iPhone/i,
      /iPod/i,
      /BlackBerry/i,
      /Windows Phone/i,
      /Opera Mini/i,
      /webOS/i,
    ];

    const userAgentIsMobile = mobileUserAgents.some((regex) => regex.test(userAgent.value));

    // Detección por tamaño de pantalla (más simple y confiable)
    const isSmallScreen = screenWidth.value <= 768;

    // Es móvil si el user agent lo indica o si es pantalla pequeña con touch
    return userAgentIsMobile || (isSmallScreen && touchSupport.value);
  });

  const isTablet = computed(() => {
    if (!isInitialized.value) return false;

    const tabletUserAgents = [/iPad/i, /Android(?!.*Mobile)/i, /Tablet/i];

    const userAgentIsTablet = tabletUserAgents.some((regex) => regex.test(userAgent.value));
    const isTabletSize = screenWidth.value > 768 && screenWidth.value <= 1024;

    // Es tablet si el user agent lo indica o si tiene tamaño de tablet con touch
    return userAgentIsTablet || (touchSupport.value && isTabletSize);
  });

  const isDesktop = computed(() => {
    return isInitialized.value && !isMobile.value && !isTablet.value;
  });

  const deviceType = computed(() => {
    if (isMobile.value) return 'mobile';
    if (isTablet.value) return 'tablet';
    return 'desktop';
  });

  const isPortrait = computed(() => {
    return orientation.value === 'portrait' || screenHeight.value > screenWidth.value;
  });

  const isLandscape = computed(() => {
    return orientation.value === 'landscape' || screenWidth.value > screenHeight.value;
  });

  // Acciones
  const detectDevice = () => {
    if (typeof window === 'undefined') return;

    // User Agent
    userAgent.value = navigator.userAgent;

    // Dimensiones de pantalla
    screenWidth.value = window.innerWidth;
    screenHeight.value = window.innerHeight;
    devicePixelRatio.value = window.devicePixelRatio || 1;

    // Soporte táctil
    touchSupport.value =
      'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;

    maxTouchPoints.value = navigator.maxTouchPoints || navigator.msMaxTouchPoints || 0;

    // Orientación
    if (screen.orientation) {
      orientation.value = screen.orientation.type.includes('portrait') ? 'portrait' : 'landscape';
    } else {
      orientation.value = screenHeight.value > screenWidth.value ? 'portrait' : 'landscape';
    }

    isInitialized.value = true;
  };

  const updateScreenSize = () => {
    if (typeof window === 'undefined') return;

    screenWidth.value = window.innerWidth;
    screenHeight.value = window.innerHeight;

    // Actualizar orientación
    if (screen.orientation) {
      orientation.value = screen.orientation.type.includes('portrait') ? 'portrait' : 'landscape';
    } else {
      orientation.value = screenHeight.value > screenWidth.value ? 'portrait' : 'landscape';
    }
  };

  const initializeListeners = () => {
    if (typeof window === 'undefined') return;

    // Listener para cambios de tamaño de ventana
    window.addEventListener('resize', updateScreenSize);

    // Listener para cambios de orientación
    if (screen.orientation) {
      screen.orientation.addEventListener('change', () => {
        setTimeout(() => {
          updateScreenSize();
        }, 100); // Pequeño delay para que la orientación se actualice completamente
      });
    } else {
      window.addEventListener('orientationchange', () => {
        setTimeout(() => {
          updateScreenSize();
        }, 100);
      });
    }
  };

  const removeListeners = () => {
    if (typeof window === 'undefined') return;

    window.removeEventListener('resize', updateScreenSize);

    if (screen.orientation) {
      screen.orientation.removeEventListener('change', updateScreenSize);
    } else {
      window.removeEventListener('orientationchange', updateScreenSize);
    }
  };

  // Información detallada del dispositivo
  const getDeviceInfo = computed(() => ({
    userAgent: userAgent.value,
    screenWidth: screenWidth.value,
    screenHeight: screenHeight.value,
    devicePixelRatio: devicePixelRatio.value,
    touchSupport: touchSupport.value,
    maxTouchPoints: maxTouchPoints.value,
    orientation: orientation.value,
    deviceType: deviceType.value,
    isMobile: isMobile.value,
    isTablet: isTablet.value,
    isDesktop: isDesktop.value,
    isPortrait: isPortrait.value,
    isLandscape: isLandscape.value,
    isInitialized: isInitialized.value,
  }));

  return {
    // Estado
    userAgent,
    screenWidth,
    screenHeight,
    devicePixelRatio,
    touchSupport,
    orientation,
    maxTouchPoints,
    isInitialized,

    // Computed
    isMobile,
    isTablet,
    isDesktop,
    deviceType,
    isPortrait,
    isLandscape,
    getDeviceInfo,

    // Acciones
    detectDevice,
    updateScreenSize,
    initializeListeners,
    removeListeners,
  };
});
